import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import { useLocation, useNavigate } from "react-router-dom";
import apiService from "../../../services/api";
import useCodeCommon from "../../../hooks/useCodeCommon";
import useCodeCommonByCategory from "../../../hooks/useCodeCommonByCategory";
import useHubManagers from "../../../hooks/useHubManagers";
import useHubs from "../../../hooks/useHubs";
import useRoleBasedHubFiltering from "../../../hooks/useRoleBasedHubFiltering";
import {
  FormSection,
  TextInput,
  SelectInput,
  SearchableSelectInput,
  MobileInput,
  FileUploadInput,
} from "../../../components/forms";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  TextField,
} from "@mui/material";
import ImagePreviewCard from "../../../components/common/ImagePreviewCard";
import DefaultImage from "../../../assets/images/DefaultImage.png";

// Image base URL
const BASE_IMAGE_URL = process.env.REACT_APP_BASE_IMAGE_URL;
const DEFAULT_IMAGE = "default.png";

const VEHICLE_STATUS_UI_ALLOWLIST = new Set(
  ["Active", "Under Maintenance", "B2B", "Inactive"].map((s) => s.toLowerCase())
);

// --- Reusable Components ---

// --- Main Component ---

const CreateVehiclePage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    // Basic Information
    vehiclename: "",
    model: "",
    speedtype: "",
    range: "",
    topspeed: "",
    weight: "",
    mileagekm: "",
    batterycapacitykwh: "",
    rangeperchargekm: "",
    motorpower: "",
    chargingtime: "",
    color: "",

    // Registration & Connection
    registrationnumber: "",
    telematicsdeviceid: "",
    hubmasterid: "",
    assignedHub: "", // For display purposes
    status: "",
    vehicletype: "With Battery",
    vinnumber: "",
    vehiclenum: "",
    motorid: "",

    // Battery Information
    batteryno1: "",
    batteryno2: "",
    chargeno: "",

    // Specification
    otherspecifications: "",

    // Uploads - now support multiple images
    vehicleimages: [] as string[],
    batteryimages: [] as string[],
    chargerimages: [] as string[],

    id: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { options: allVehicleStatusOptions, loading: vehicleStatusLoading, error: vehicleStatusError } = useCodeCommon("VehicleStatus");

  const vehicleStatusOptions = allVehicleStatusOptions.filter((option) =>
    VEHICLE_STATUS_UI_ALLOWLIST.has(String(option.codedisplayname || "").trim().toLowerCase())
  );
  const { options: vehicleTypeOptions, loading: vehicleTypeLoading, error: vehicleTypeError } = useCodeCommon("VehicleType");
  const { options: evSpeedTypeOptions, loading: evSpeedTypeLoading, error: evSpeedTypeError } = useCodeCommon("EVSpeedType");
  const { statusOptions: vehicleModelOptions, loading: vehicleModelLoading, error: vehicleModelError, getCodeValueByDisplayName: getVehicleModelCodeValue } = useCodeCommonByCategory("VehicleModel");
  const { hubManagers, loading: hubManagersLoading, error: hubManagersError } = useHubManagers();
  const { hubOptions, loading: hubsLoading } = useHubs(setError);
  const { filterHubOptions } = useRoleBasedHubFiltering();

  // Filter hub options based on user role
  const filteredHubOptions = filterHubOptions(hubOptions);

  // Create page does not preload edit data
  const [imageModal, setImageModal] = useState<{ open: boolean; src: string; title: string }>({ open: false, src: "", title: "" });

  // Normalize image value to a clean path like "/Miscellaneous/abc.png"
  const normalizeImagePath = (value: string): string => {
    try {
      const trimmed = String(value || "").trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("[")) {
        const arr = JSON.parse(trimmed);
        if (Array.isArray(arr) && arr.length > 0) {
          return String(arr[0] || "");
        }
      }
      const lower = trimmed.toLowerCase();
      const marker = "/commonservice/images/";
      const idx = lower.indexOf(marker);
      if (idx !== -1) {
        const after = trimmed.substring(idx + marker.length);
        return after.startsWith("/") ? after : `/${after}`;
      }
      return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    } catch {
      return String(value || "");
    }
  };

  const handleInputChange = (field: string, value: string | string[]) => {
    // Clear field error on valid input
    setErrors((prev) => {
      const next = { ...prev };
      const str = Array.isArray(value) ? value.join("") : String(value ?? "");
      if (str.trim() !== "") delete (next as any)[field];
      // If vehicletype toggles to Without Battery, clear battery-related errors
      if (field === "vehicletype" && value === "Without Battery") {
        delete next.batteryno1;
        delete next.batteryno2;
        delete next.chargeno;
        delete next.batteryimages;
        delete next.chargerimages;
      }
      if (field === "speedtype") {
        const speedTypeStr = String(value ?? "").toLowerCase();
        if (speedTypeStr.includes("low")) {
          delete next.vinnumber;
          delete next.registrationnumber;
        }
      }
      return next;
    });

      if (field === "speedtype") {
        const speedTypeStr = String(value ?? "").toLowerCase();
        if (speedTypeStr.includes("low")) {
          setFormData((prev) => ({ ...prev, vinnumber: "", registrationnumber: "", batteryno2: "" }));
          delete (errors as any).batteryno2;
        }
      }

    if (field === "assignedHub") {
      // Find the hubmasterid for the selected hub name
      const selectedHub = hubOptions.find(hub => hub.hubname === value);
      setFormData((prev) => ({ 
        ...prev, 
        assignedHub: value as string,
        hubmasterid: selectedHub ? selectedHub.hubmasterid.toString() : ""
      }));
    } else if (field === "vehicleimages" || field === "batteryimages" || field === "chargerimages") {
      // Handle image array fields - append new images to existing array
      const newImages = Array.isArray(value) ? value : (value ? [value] : []);
      setFormData((prev) => {
        const existingImages = Array.isArray(prev[field as keyof typeof prev]) ? prev[field as keyof typeof prev] as string[] : [];
        return { ...prev, [field]: [...existingImages, ...newImages] };
      });
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const resolvePreviewSrc = (value: string): string => {
    const v = String(value || "").trim();
    if (!v) return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
    if (v.startsWith('http')) return v;
    const cleaned = v.replace(/^\/+/, '');
    return `${BASE_IMAGE_URL}${cleaned}`;
  };

  const handlePreview = (title: string, value: string) => {
    setImageModal({ open: true, src: resolvePreviewSrc(value), title });
  };

  const handleDownload = (value: string) => {
    const url = resolvePreviewSrc(value);
    const link = document.createElement('a');
    link.href = url;
    link.download = (value || 'image').toString().split('/').pop() || 'image';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteImage = (field: 'vehicleimages' | 'batteryimages' | 'chargerimages') => {
    setFormData((prev) => ({ ...prev, [field]: [] }));
    setErrors((prev) => {
      const next = { ...prev };
      delete (next as any)[field];
      return next;
    });
  };

  const handleDeleteImageFromArray = (field: 'vehicleimages' | 'batteryimages' | 'chargerimages', index: number) => {
    setFormData((prev) => {
      const currentArray = (prev[field] as string[]) || [];
      const newArray = currentArray.filter((_, i) => i !== index);
      return { ...prev, [field]: newArray };
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete (next as any)[field];
      return next;
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validate required fields (all except otherspecifications)
      const nextErrors: Record<string, string> = {};
      const isWithoutBattery = formData.vehicletype === "Without Battery";

      const requireField = (key: string, label: string) => {
        const value = (formData as any)[key];
        if (value === undefined || value === null || String(value).trim() === "") {
          nextErrors[key] = `${label} is required`;
        }
      };

      // Basic Information
      requireField("vehiclename", "Vehicle Name");
      requireField("model", "Model");
      requireField("speedtype", "Speed Type");
      requireField("range", "Range");
      requireField("topspeed", "Top Speed");
      requireField("weight", "Weight");

      // Registration & Connection
      const isHighSpeed = formData.speedtype && formData.speedtype.toLowerCase().includes("high");
      if (isHighSpeed) {
        requireField("registrationnumber", "Registration No.");
        requireField("vinnumber", "VIN Number");
      }
      requireField("telematicsdeviceid", "Telematics Device ID");
      requireField("vehiclenum", "Vehicle Number");
      requireField("assignedHub", "Assign Hub");
      requireField("status", "Status");
      if (!isHighSpeed) {
        requireField("motorid", "Motor ID");
      }

      // Uploads - check if at least one image is uploaded
      if (!formData.vehicleimages || formData.vehicleimages.length === 0) {
        nextErrors.vehicleimages = "At least one vehicle image is required";
      }
      if (!isWithoutBattery) {
        // Battery Information
        requireField("batteryno1", "Battery No 1");
        if (isHighSpeed) {
          requireField("batteryno2", "Battery No 2");
        }
        requireField("chargeno", "Charge No");
        // Battery uploads - check if at least one image is uploaded
        if (!formData.batteryimages || formData.batteryimages.length === 0) {
          nextErrors.batteryimages = "At least one battery image is required";
        }
        if (!formData.chargerimages || formData.chargerimages.length === 0) {
          nextErrors.chargerimages = "At least one charger image is required";
        }
      }

      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) {
        throw new Error("Please fill all required fields");
      }

      // Prepare data for API
      const apiData = {
        vehicleid: 0,
        vehiclename: formData.vehiclename,
        vehiclemodel: (() => {
          const modelCodeValue = getVehicleModelCodeValue(formData.model);
          return modelCodeValue ? parseInt(modelCodeValue) : 0;
        })(),
        topspeed: parseFloat(formData.topspeed) || 0,
        weight: formData.weight ? parseFloat(formData.weight) : 0,
        speedtype: (() => {
          const speedTypeOption = evSpeedTypeOptions.find(s => s.codedisplayname === formData.speedtype);
          return speedTypeOption ? parseInt(speedTypeOption.codevalue) : 1;
        })(),
        batterycapacitykwh: 0, // Not used in current form
        rangeperchargekm: parseFloat(formData.range) || 0,
        motorpower: "", // Not used in current form
        chargingtime: "", // Not used in current form
        color: "", // Not used in current form
        registrationnumber: formData.registrationnumber,
        telematicsdeviceid: formData.telematicsdeviceid,
        hubmasterid: parseInt(formData.hubmasterid) || 0,
        status: (() => {
          const statusOption = vehicleStatusOptions.find(s => s.codedisplayname === formData.status);
          return statusOption ? parseInt(statusOption.codevalue) : 1;
        })(),
        userid: 0,
        vehicleimages: (() => {
          // Combine all image arrays into one
          const allImages = [
            ...(formData.vehicleimages || []),
            ...(formData.batteryimages || []),
            ...(formData.chargerimages || [])
          ];
          const paths = allImages
            .map(v => normalizeImagePath(v as string))
            .filter(p => Boolean(p));
          return paths as string[];
        })(),
        vehicletype: (() => {
          const vehicleTypeOption = vehicleTypeOptions.find(t => t.codedisplayname === formData.vehicletype);
          return vehicleTypeOption ? parseInt(vehicleTypeOption.codevalue) : 1;
        })(),
        vinnumber: formData.vinnumber,
        vehiclenum: formData.vehiclenum,
        motorid: formData.motorid,
        batteryno1: formData.batteryno1,
        batteryno2: formData.batteryno2,
        chargeno: formData.chargeno,
        otherspecifications: formData.otherspecifications,
      };

      await apiService.createVehicle(apiData);
      
      // Navigate back and signal list to refresh
      navigate('/vehicle-management', { state: { refresh: Date.now() } });
    } catch (err: any) {
      console.error('Error saving vehicle:', err);
      setError(err.message || "Failed to save vehicle");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid>
      {/* Error Display */}
      {(error || vehicleStatusError || vehicleTypeError || evSpeedTypeError || vehicleModelError || hubManagersError) && (
        <Alert 
          severity="error" 
          className="mb-3" 
          onClose={() => setError(null)}
        >
          {error || vehicleStatusError || vehicleTypeError || evSpeedTypeError || vehicleModelError || hubManagersError}
        </Alert>
      )}

      <FormSection>
        <Typography variant="subtitle2" className="mb-2" sx={{ color: 'rgba(22, 67, 135, 1)', fontWeight: 600 }}>
          Basic Information
        </Typography>
        <Row className="g-3">
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="vehiclename"
              formData={formData}
              onChange={handleInputChange}
              label="Vehicle Name"
              required
              error={errors.vehiclename}
              placeholder="Enter vehicle name"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <SearchableSelectInput
              field="model"
              formData={formData}
              onChange={handleInputChange}
              label="Model"
              required
              error={errors.model}
              disabled={vehicleModelLoading}
              loading={vehicleModelLoading}
              placeholder="Search and select model"
              options={vehicleModelOptions || []}
            />
          </Col>
            <Col xs={12} md={6} lg={3}>
              <SelectInput
                field="speedtype"
                formData={formData}
                onChange={handleInputChange}
                label="Speed Type"
              required
              error={errors.speedtype}
              disabled={evSpeedTypeLoading}
              options={evSpeedTypeOptions.map(speedType => speedType.codedisplayname)}
              />
            </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="range"
              formData={formData}
              onChange={handleInputChange}
              label="Range (km)"
              required
              error={errors.range}
              placeholder="Enter range"
              type="number"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="topspeed"
              formData={formData}
              onChange={handleInputChange}
              label="Top Speed (kmph)"
              required
              error={errors.topspeed}
              placeholder="Enter top speed"
              type="number"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="weight"
              formData={formData}
              onChange={handleInputChange}
              label="Weight"
              required
              error={errors.weight}
              placeholder="Enter weight"
              type="number"
            />
          </Col>
        </Row>
      </FormSection>

      <FormSection>
          <Typography variant="subtitle2" className="mb-2" sx={{ color: 'rgba(22, 67, 135, 1)', fontWeight: 600 }}>
          Registration & Connection
        </Typography>
        <Row className="g-3">
          {formData.speedtype && formData.speedtype.toLowerCase().includes("high") && (
            <Col xs={12} md={6} lg={3}>
              <TextInput
                field="registrationnumber"
                formData={formData}
                onChange={handleInputChange}
                label="Registration No."
                required
                error={errors.registrationnumber}
                placeholder="Enter registration no."
              />
            </Col>
          )}
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="vehiclenum"
              formData={formData}
              onChange={handleInputChange}
              label="Vehicle Number"
              required
              error={errors.vehiclenum}
              placeholder="Enter vehicle number"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="telematicsdeviceid"
              formData={formData}
              onChange={handleInputChange}
              label="Telematics Device ID"
            required
            error={errors.telematicsdeviceid}
              placeholder="Enter telematics device ID"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <SearchableSelectInput
              field="assignedHub"
              formData={formData}
              onChange={handleInputChange}
              label="Assign Hub"
              required
            error={errors.assignedHub}
              disabled={hubsLoading}
              placeholder="Search and select hub"
              options={
                hubsLoading
                  ? []
                  : filteredHubOptions.map((hub: any) => hub.hubname)
              }
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <SelectInput
              field="status"
              formData={formData}
              onChange={handleInputChange}
              label="Status"
            required
            error={errors.status}
              disabled={vehicleStatusLoading}
              options={vehicleStatusOptions.map(status => status.codedisplayname)}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <SelectInput
              field="vehicletype"
              formData={formData}
              onChange={handleInputChange}
              label="Vehicle Type"
            required
            error={errors.vehicletype}
              disabled={vehicleTypeLoading}
              options={vehicleTypeOptions.map(vehicleType => vehicleType.codedisplayname)}
            />
          </Col>
          {formData.speedtype && formData.speedtype.toLowerCase().includes("high") && (
            <Col xs={12} md={6} lg={3}>
              <TextInput
                field="vinnumber"
                formData={formData}
                onChange={handleInputChange}
                label="VIN Number"
                required
                error={errors.vinnumber}
                placeholder="Enter VIN number"
              />
            </Col>
          )}
          
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="motorid"
              formData={formData}
              onChange={handleInputChange}
              label="Motor ID"
              required={Boolean(formData.speedtype && formData.speedtype.toLowerCase().includes("low"))}
              error={errors.motorid}
              placeholder="Enter Motor ID"
            />
          </Col>
        </Row>
      </FormSection>

      {formData.vehicletype !== "Without Battery" && (
        <FormSection>
          <Typography variant="subtitle2" className="mb-2" sx={{ color: 'rgba(22, 67, 135, 1)', fontWeight: 600 }}>
            Battery Information
          </Typography>
          <Row className="g-3">
            <Col xs={12} md={6} lg={3}>
              <TextInput
                field="batteryno1"
                formData={formData}
                onChange={handleInputChange}
                label="Battery No 1."
                required
                error={errors.batteryno1}
                placeholder="Enter battery no."
              />
            </Col>
            {formData.speedtype && formData.speedtype.toLowerCase().includes("high") && (
              <Col xs={12} md={6} lg={3}>
                <TextInput
                  field="batteryno2"
                  formData={formData}
                  onChange={handleInputChange}
                  label="Battery No 2."
                  required
                  error={errors.batteryno2}
                  placeholder="Enter battery no."
                />
              </Col>
            )}
            <Col xs={12} md={6} lg={3}>
              <TextInput
                field="chargeno"
                formData={formData}
                onChange={handleInputChange}
                label="Charger No."
                required
                error={errors.chargeno}
                placeholder="Enter charger no."
              />
            </Col>
          </Row>
        </FormSection>
      )}

      <FormSection>
        <Typography variant="subtitle2" className="mb-2" sx={{ color: 'rgba(22, 67, 135, 1)', fontWeight: 600 }}>
          Specification
        </Typography>
        <Row className="g-3">
          <Col xs={12}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Other Information"
              placeholder="Enter other information"
              value={formData.otherspecifications}
              onChange={(e) => handleInputChange("otherspecifications", e.target.value)}
              multiline
              rows={4}
              sx={{
                "& .MuiFormLabel-asterisk": {
                  color: "red",
                },
              }}
            />
          </Col>
        </Row>
      </FormSection>

      <FormSection>
        <Typography variant="subtitle2" className="mb-2" sx={{ color: 'rgba(22, 67, 135, 1)', fontWeight: 600 }}>
          Attachments
        </Typography>
        <Row className="g-3">
          <Col xs={12} md={4}>
            <FileUploadInput
              field="vehicleimages"
              formData={formData}
              onChange={handleInputChange}
              label="Upload Vehicle Images"
              folderPath="vehicle"
              folderType="vehicle"
              required
              multiple
              maxFiles={5}
              error={errors.vehicleimages}
            />
          </Col>
          {formData.vehicletype !== "Without Battery" && (
            <>
              <Col xs={12} md={4}>
                <FileUploadInput
                  field="batteryimages"
                  formData={formData}
                  onChange={handleInputChange}
                  label="Upload Battery Images"
                  folderPath="battery"
                  folderType="battery"
                  required
                  error={errors.batteryimages}
                  multiple
                  maxFiles={2}
                />
              </Col>
              <Col xs={12} md={4}>
                <FileUploadInput
                  field="chargerimages"
                  formData={formData}
                  onChange={handleInputChange}
                  label="Upload Charger Images"
                  folderPath="charger"
                  folderType="charger"
                  required
                  error={errors.chargerimages}
                  multiple
                  maxFiles={2}
                />
              </Col>
            </>
          )}
        </Row>
        
        {/* Image Preview Section */}
        {((Array.isArray(formData.vehicleimages) && formData.vehicleimages.length > 0) || 
          (Array.isArray(formData.batteryimages) && formData.batteryimages.length > 0) || 
          (Array.isArray(formData.chargerimages) && formData.chargerimages.length > 0)) && (
          <Row className="g-3 mt-3">
            <Col xs={12}>
              <Typography variant="body2" color="textSecondary" className="mb-2">
                Uploaded Images
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {/* Vehicle Images */}
                {Array.isArray(formData.vehicleimages) && formData.vehicleimages.map((image, index) => (
                  <ImagePreviewCard
                    key={`vehicle-${index}`}
                    title={`Vehicle Image ${index + 1}`}
                    value={image}
                    resolveSrc={resolvePreviewSrc}
                    onDelete={() => handleDeleteImageFromArray('vehicleimages', index)}
                    size={200}
                    defaultImage={DefaultImage}
                  />
                ))}
                
                {/* Battery Images */}
                {formData.vehicletype !== "Without Battery" && Array.isArray(formData.batteryimages) && formData.batteryimages.map((image, index) => (
                  <ImagePreviewCard
                    key={`battery-${index}`}
                    title={`Battery Image ${index + 1}`}
                    value={image}
                    resolveSrc={resolvePreviewSrc}
                    onDelete={() => handleDeleteImageFromArray('batteryimages', index)}
                    size={200}
                    defaultImage={DefaultImage}
                  />
                ))}
                
                {/* Charger Images */}
                {formData.vehicletype !== "Without Battery" && Array.isArray(formData.chargerimages) && formData.chargerimages.map((image, index) => (
                  <ImagePreviewCard
                    key={`charger-${index}`}
                    title={`Charger Image ${index + 1}`}
                    value={image}
                    resolveSrc={resolvePreviewSrc}
                    onDelete={() => handleDeleteImageFromArray('chargerimages', index)}
                    size={200}
                    defaultImage={DefaultImage}
                  />
                ))}
              </Box>
            </Col>
          </Row>
        )}
      </FormSection>

      {imageModal.open && (
        <Box onClick={() => setImageModal({ open: false, src: '', title: '' })} sx={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <Box sx={{ backgroundColor: '#000', borderRadius: 1, maxWidth: '90vw', maxHeight: '90vh', p: 1 }}>
            <img src={imageModal.src} alt={imageModal.title} style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block' }} />
          </Box>
        </Box>
      )}

      {/* Action Buttons */}
      <Box className="d-flex justify-content-end gap-3 mt-4">
        <Button
          variant="contained"
          className="px-2 py-2 fw-medium text-white"
          onClick={handleSave}
          disabled={loading}
          sx={{
            backgroundColor: "rgba(13,44,90,1)",
            minWidth: "130px",
            "&:disabled": {
              backgroundColor: "rgba(13,44,90,0.5)",
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Saving...
            </Box>
          ) : (
            'Save'
          )}
        </Button>
        <Button
          variant="outlined"
          className="px-4 py-2 fw-medium"
          onClick={onBack}
          sx={{
            borderColor: "rgba(13,44,90,1)",
            color: "rgba(13,44,90,1)",
            "&:hover": {
              borderColor: "rgba(13,44,90,1)",
              backgroundColor: "rgba(13,44,90,0.04)",
            },
          }}
        >
          Cancel
        </Button>
      </Box>
    </Container>
  );
};

export default CreateVehiclePage;



